// Code-to-copy table (UX spec section 8). Server messages are already safe; this
// table only adds the "what to do" half where the code is known.
import { ApiRequestError, isApiRequestError } from './api.ts';

const COPY: Record<string, string> = {
  unauthenticated: 'Your session ended. Sign in again.',
  csrf_invalid: 'Your session security token was refreshed. Try the action again.',
  origin_denied: 'This page is not on an allowed address for the API.',
  origin_required: 'This browser did not send a trusted origin. Reload the page.',
  tenant_required: 'Choose a workspace to continue.',
  permission_denied: "Your role doesn't allow this action. Ask an admin if you need it.",
  not_found: 'Not found or not shared with you.',
  version_conflict: 'This changed since you opened it. Review the latest version, then try again.',
  state_conflict: 'The call changed state. Review the latest call, then try again.',
  idempotency_conflict: 'That request was already used for a different action. Start the action again.',
  invalid_input: 'Some details are not valid. Check the highlighted fields.',
  idempotency_key_required: 'The request was missing its safety key. Reload and try again.',
  rate_limited: 'Too many requests. Wait a moment, then try again.',
  login_limited: 'Sign-in limit reached. Wait 15 minutes, then try again.',
  invalid_credentials: "The email, password or verification code isn't right.",
  mfa_required: 'Enter the 6-digit code from your authenticator app.',
  mfa_enrollment_required: 'Your role requires two-step verification. Ask an administrator to set it up for you.',
  telephony_unconfigured: 'Your phone connection is not ready yet. An administrator needs to finish setup.',
  telephony_unavailable: 'The phone system is unavailable right now. Try again shortly.',
  pstn_disabled: 'Calls to outside numbers are turned off for this workspace.',
  pstn_unconfigured: 'Calling outside numbers isn’t set up yet.',
  pstn_permission_required: 'Your role doesn’t include calling outside numbers. Ask an admin if you need it.',
  pstn_line_required: 'Choose a number you’re allowed to call from. An admin assigns numbers for outside calls.',
  pstn_toll_free_blocked: 'Toll-free numbers (800, 888, 877, 866, 855, 844 and 833) are blocked for outbound prospecting.',
  pstn_destination_not_allowed: 'Calls to this number aren’t allowed by your workspace.',
  pstn_daily_cap_reached: 'Your workspace reached today’s limit for outside calls. An admin can review limits.',
  pstn_line_daily_limit_reached: 'All available outgoing numbers have reached today’s 40-call warming limit. They reset at midnight local time.',
  pstn_concurrency_limit: 'Too many outside calls are in progress. Try again when one ends.',
  pstn_kill_switch: 'Outside calling was stopped by an admin. Calls to teammates still work.',
  pstn_max_duration: 'The workspace’s time limit for outside calls stopped this call.',
  destination_blocked: 'This number is on your workspace’s do-not-call list. An admin can change that in Setup, Blocked numbers.',
  device_not_ready: "Your phone isn't connected to the phone system yet. Try again in a moment.",
  agent_busy: 'You or the person you called already has a call in progress.',
  invalid_destination: 'Choose someone else to call.',
  device_limit: 'You have too many registered devices. Revoke one in Settings first.',
  connector_owned: 'This contact is managed in the CRM and can’t be edited here.',
  contact_in_use: 'This contact is linked to call history and can’t be deleted.',
  cursor_invalid: 'The list changed. Reload it to continue.',
  archive_unconfigured: 'Recording playback isn’t set up for this workspace.',
  media_grant_required: 'You don’t have access to this recording or transcript. Ask an administrator if you need it.',
  network_error: "Couldn't reach the server. Check your connection, then try again.",
  desktop_pkce_unimplemented: 'Desktop sign-in through the browser isn’t available yet.',
  desktop_new_authorization_required: 'To use another workspace in Sidetone desktop, sign out, then sign in again in your browser and choose that workspace.',
  view_as_read_only: 'You are viewing Sidetone as another user in read-only mode. Exit view-as mode to make changes.',
  internal_error: 'Something went wrong on the server.',
};

export interface ErrorView {
  title: string;
  detail: string;
  code: string;
  requestId: string | null;
  retryable: boolean;
  /** HTTP status of the failed request, when there was one (0 for network failures). */
  status?: number;
}

export function describeError(error: unknown, fallbackTitle = 'Something went wrong'): ErrorView {
  if (isApiRequestError(error)) {
    const hint = COPY[error.code];
    return {
      title: hint ?? fallbackTitle,
      detail: hint && hint !== error.message ? error.message : '',
      code: error.code,
      requestId: error.requestId,
      retryable: error.retryable,
      status: error.status,
    };
  }
  return { title: fallbackTitle, detail: '', code: 'client_error', requestId: null, retryable: true };
}

export function errorSentence(error: unknown, fallback = 'Something went wrong.'): string {
  const view = describeError(error, fallback);
  const ref = view.requestId ? ` Reference ${view.requestId}.` : '';
  return `${view.title}${ref}`;
}

/** Known copy for a server error code, or the fallback. */
export function copyForCode(code: string, fallback: string): string {
  return COPY[code] ?? fallback;
}

export function fieldErrors(error: unknown): Record<string, string> {
  return error instanceof ApiRequestError && error.fields ? { ...error.fields } : {};
}
